import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { filter, map, take } from 'rxjs/operators';

import { AuthService } from '@app/core/services/auth.service';
import { BlockModalService } from '@app/core/services/block-modal.service';

/**
 * CB — Client Blocking (2026-06-30, AD-049 / CB-LD-9 / CB-LD-18).
 *
 * Blocks navigation for a `partially_blocked` user, with a small
 * allowlist of routes that remain reachable (see below).
 *
 * When the guard rejects:
 *   - Opens the consent modal via `BlockModalService.open(...)`.
 *   - Returns `false` (stays on the current page; does NOT redirect
 *     elsewhere per CB-LD-18 — the user's last successful route is
 *     preserved).
 *
 * Layered after `authGuard` / `maintenanceGuard` — if no user is loaded
 * the guard passes through (`true`) so `authGuard` drives the
 * login redirect.
 *
 * Page-refresh race: when `restoreInProgress()` is true we await its
 * completion before deciding — same pattern as [[authGuard]] and
 * [[kycRequiredGuard]].
 */
export const partialBlockGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const blockModalService = inject(BlockModalService);

  const evaluate = (): boolean => {
    const user = authService.getUser();
    if (!user) return true; // defer to authGuard
    if (user.blockStatus !== 'partially_blocked') return true;

    // CB-LD-10 allowlist (expanded 2026-06-30 follow-up). Routes a
    // `partially_blocked` user MAY reach without triggering the consent
    // modal:
    //   - `/kyc` + `/corporate/kyc` — keep progressing verification.
    //   - `/dashboard` + `/corporate/dashboard` — read-only landing.
    //   - `/support` + `/corporate/support` — Help & Support so the user
    //     can actually contact us (the modal tells them to).
    // Everything else (transfers, withdrawals, profile, settings,
    // accounts, deposits, beneficiaries, history, corporate users /
    // approvals) remains gated; the modal opens on attempted navigation.
    if (
      state.url.startsWith('/kyc') ||
      state.url.startsWith('/corporate/kyc') ||
      state.url.startsWith('/dashboard') ||
      state.url.startsWith('/corporate/dashboard') ||
      state.url.startsWith('/support') ||
      state.url.startsWith('/corporate/support')
    ) {
      return true;
    }

    blockModalService.open('partially_blocked', user.blockReason);
    return false;
  };

  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => evaluate()),
    );
  }

  return evaluate();
};
